/**
 * The token to be used by actions in order to apply the API middleware.
 */
export const ApiToken = Symbol('ApiToken');

export const API_PATH = '/api';

/**
 * Valid responseType values for API calls. responseType is a hint to the API
 * middleware what response type we're expecting from a call; affects what
 * 'Content-Type' header gets set, if at all.
 */
export const ApiResponseTypes = Object.freeze({
  JSON: 'json',
  PLAINTEXT: 'plaintext',
} as const);

export const ServiceHostTypes = Object.freeze({
  AB: 'AB',
  COMMUNITY_SSO: 'COMMUNITY_SSO',
  DEFAULT: 'DEFAULT',
  PAID: 'PAID',
  SEARCH_AUTOCOMPLETE: 'SEARCH_AUTOCOMPLETE',
  SHEER_ID: 'SHEER_ID',
  STATS: 'STATS',
  TWITTER: 'TWITTER',
} as const);

export const ApiStatusSuffixes = ['ATTEMPT', 'SUCCESS', 'FAIL'] as const;
export type ApiStatusSuffix = typeof ApiStatusSuffixes[number];

// Keyed by the corresponding "action prefix"
export const ApiEndpoints = Object.freeze({
  REGISTER: 'v1/listener/register',
  EMAIL_PASSWORD: 'v1/listener/emailPassword',
  VALIDATE_PASSWORD_RESET_TOKEN: 'v1/listener/validatePasswordResetToken',
  RESET_PASSWORD: 'v1/listener/resetPassword',
  UPDATE_ACCOUNT: 'v1/listener/updateAccount',
  ADD_TRACKING_CODE: 'v1/listener/addTrackingCode',
  SET_EXPLICIT_PIN: 'v1/listener/setExplicitPIN',
  EMAIL_EXPLICIT_PIN: 'v1/listener/emailExplicitPINRescue',
  UPDATE_SETTINGS: 'v1/listener/updateSettings',
  GET_PROFILE: 'v1/listener/getProfile',
  SET_PROFILE: 'v1/listener/setProfile',
  ADD_TIRED_SONG: 'v3/station/addTiredSong',
  ACCOUNT_MESSAGE_DISMISSED: 'v1/listener/accountMessageDismissed',
  LOGIN: 'v1/auth/login',
  LOGOUT: 'v1/auth/logout',
  DEVICE_LOGIN: 'v1/auth/deviceLogin',
  ANON_LOGIN: 'v1/auth/anonymousLogin',
  FETCH_GENRE_CATEGORIES: 'v1/music/genrecategories',
  FETCH_GENRES_BY_CATEGORY_ID: 'v1/music/genres',
  FETCH_FULL_LYRICS: 'v4/catalog/getFullLyricsV2',
  GET_FRAGMENT: 'v1/playlist/getFragment',
  FETCH_STATIONS: 'v1/station/getStations',
  CREATE_STATION: 'v1/station/createStation',
  REMOVE_STATION: 'v1/station/removeStation',
  UPDATE_STATION: 'v1/station/updateStation',
  ADD_SEED: 'v1/station/addSeed',
  GET_OR_SET_SHUFFLE_STATIONS: 'v1/station/shuffle',
  DELETE_SEED: 'v1/station/deleteSeed',
  FETCH_NARRATIVE: 'v1/playlist/narrative',
  ADD_FEEDBACK: 'v1/station/addFeedback', // cspell:disable-line
  GET_FEEDBACK: 'v1/station/getFeedback',
  DELETE_FEEDBACK: 'v1/station/deleteFeedback', // cspell:disable-line
  INSERT_ARTIST_MESSAGE_METRIC: 'v1/amp/insertArtistMessageMetric',
  INSERT_VOICE_TRACK_METRIC: 'v1/mip/insertVoiceTrackMetric',
  GET_ARTIST_EVENTS: 'v1/mip/getArtistPageConcerts',
  FLAG_MESSAGE: 'v1/amp/flagMessage',
  UPDATE_SHUFFLE_STATION: 'v1/station/updateShuffleStation',
  VERIFY_HYBRID_STATION_CHECKSUM: 'v1/station/verifyHybridStationChecksum',
  TRACK_STARTED: 'v1/station/trackStarted',
  PLAYBACK_PAUSED: 'v1/station/playbackPaused',
  PLAYBACK_RESUMED: 'v1/station/playbackResumed',
  ARTIST_SHARE: 'v1/share/artist',
  SEARCH: 'v1/search/fullSearch',
  SEARCH_RECOMMENDATIONS: 'v1/search/getStationRecommendations',
  GET_ADLIST: 'v1/ad/getAdList',
  USE_SKIP_REWARD: 'v1/ad/useSkipReward',
  USE_REPLAY_REWARD: 'v1/ad/useReplayReward',
  START_VALUE_EXCHANGE: 'v1/ad/startValueExchange',
  FACEBOOK_CONNECT: 'v1/facebook/connect',
  FACEBOOK_DISCONNECT: 'v1/facebook/disconnect',
  GET_REPLAY_TRACK: 'v1/ondemand/getReplayTrack',
  REGISTER_IMPRESSION: 'v1/ad/registerImpression',
  REGISTER_VIDEO_AD_IMPRESSION: 'v1/ad/registerVideoAdImpression',
  REGISTER_AUDIO_PROGRAMMATIC_IMPRESSION: 'v1/ad/registerAudioProgrammaticImpression',
  SEND_NEW_SITE_FEEDBACK: 'v1/feedback', // All stats calls go to the same url, this "endpoint" is really just used as a differentiator. @todo https://jira.savagebeast.com/browse/WEB-8689
  DELETE_LISTENER: 'v1/listener/deleteListener',
  LOG_OUT_ALL_DEVICES: 'v1/auth/logoutAll',
  GET_SEED_SUGGESTIONS: 'v1/search/getSeedSuggestions',
  GET_STATION_DETAILS: 'v1/station/getStationDetails',
  GET_SEEDS: 'v1/station/getSeeds',
  GET_STATION_FEEDBACK: 'v1/station/getStationFeedback',
  BROKEN_AD: 'v1/ad/brokenAd',
  GET_AVAILABLE_PRODUCTS: 'v2/charon/getAvailableProducts',
  ACQUIRE_PRODUCT: 'v1/billing/acquireProduct',
  PREPARE_TO_ACQUIRE_PAYPAL_PRODUCT: 'v1/billing/prepareToAcquirePaypalProduct',
  GET_SUBSCRIPTION_INFO_V2: 'v1/billing/infoV2',
  GET_SPECIAL_OFFER_V2: 'v2/charon/getSpecialOffer',
  UPDATE_BILLING_INFO_V2: 'v1/billing/updateBillingInformationV2',
  GET_CREDIT_CARD_V2: 'v1/billing/getCreditCardV2',
  CANCEL_CURRENT_PRODUCT: 'v1/billing/cancelCurrentProduct',
  GET_EXPERIMENTS_FOR_LISTENER: 'v1/abexperiment/getListenerExperimentsByHoldoutGroups',

  // Discovery Tuner
  INTERACTIVE_RADIO_SET_MODE: 'v1/interactiveradio/setAndGetAvailableModes ',
  INTERACTIVE_RADIO_GET_MODES: 'v1/interactiveradio/getAvailableModesSimple',

  // On demand
  CATALOG_ANNOTATE_OBJECTS: 'v4/catalog/annotateObjects',
  CATALOG_ANNOTATE_OBJECTS_SIMPLE: 'v4/catalog/annotateObjectsSimple',
  CATALOG_GET_DETAILS: 'v4/catalog/getDetails',
  CATALOG_GET_ARTIST_DISCOGRAPHY: 'v4/catalog/getArtistDiscography',
  CATALOG_GET_ALL_ARTIST_TRACKS: 'v4/catalog/getAllArtistTracks',
  CATALOG_GET_DETAILS_WITH_COLLABORATIONS: 'v4/catalog/getDetailsWithCollaborations',
  CATALOG_GET_ARTIST_DISCOGRAPHY_WITH_COLLABORATIONS:
    'v4/catalog/getArtistDiscographyWithCollaborations',
  CATALOG_GET_ALL_ARTIST_TRACKS_WITH_COLLABORATIONS:
    'v4/catalog/getAllArtistTracksWithCollaborations',
  CATALOG_SORT_OBJECTS: 'v4/catalog/sortObjects',
  COLLECTIONS_GET_VERSION: 'v5/collections/getVersion',
  COLLECTIONS_GET_ITEMS: 'v6/collections/getItems',
  COLLECTIONS_GET_ITEMS_BY_ARTIST: 'v5/collections/getItemsByArtist',
  COLLECTIONS_GET_SORTED_BY_TYPES_V6: 'v6/collections/getSortedByTypes',
  COLLECTIONS_GET_SORTED_BY_TYPES: 'v7/collections/getSortedByTypes',
  COLLECTIONS_GET_SORTED_PLAYLISTS: 'v6/collections/getSortedPlaylists',
  COLLECTIONS_ADD_ITEM: 'v6/collections/addItem',
  COLLECTIONS_REMOVE_ITEM: 'v6/collections/removeItem',
  PLAYLISTS_CREATE: 'v7/playlists/create',
  PLAYLISTS_ANNOTATE_PLAYLISTS: 'v7/playlists/annotatePlaylists',
  PLAYLISTS_GET_TRACKS: 'v7/playlists/getTracks',
  PLAYLISTS_EDIT_TRACKS: 'v7/playlists/editTracks',
  PLAYLISTS_APPEND_ITEMS: 'v7/playlists/appendItems',
  PLAYLISTS_DELETE_TRACKS: 'v7/playlists/deleteTracks',
  PLAYLISTS_SET_DETAILS: 'v7/playlists/setDetails',
  SOD_SEARCH: 'v3/sod/search',
  PODS_GET_PLAYLIST_AUTOFILL_SONGS: 'v5/pods/getPlaylistAutofillSongs',
  PODS_GET_PLAYLIST_SEARCH_RECOMMENDATIONS: 'v5/pods/getPlaylistSearchRecommendations',
  SEARCH_AUTOCOMPLETE: 'autocomplete',
  SET_TWITTER_TOKENS: 'v1/twitter/setUserTokens',
  GET_REQUEST_TOKEN: 'twitter/oauth/request_token',
  GET_AUTH_TOKEN: 'twitter/oauth/access_token',
  POST_STATUS_UPDATE: 'twitter/statuses/update.json',
  SEND_STATS_EVENTS: 'stats', // All stats calls go to the same url, this "endpoint" is really just used as a differentiator. @todo https://jira.savagebeast.com/browse/WEB-8689

  // Browse v1
  GET_MUSIC_RECOMMENDATION: 'v1/browse/getMusicRecommendation',
  GET_MUSIC_CATALOG: 'v1/browse/getMusicCatalog',

  // Charon
  FAMILY_PLAN_GET_RECORD: 'v2/charon/getFamilyPlanRecord',
  FAMILY_PLAN_GET_PARENT_INFO: 'v1/charon/getParentFamilyPlanInfo',
  FAMILY_PLAN_CREATE_INVITE: 'v1/charon/createFamilyPlanInvite',
  FAMILY_PLAN_RESEND_INVITE: 'v1/charon/resendFamilyPlanInviteEmail',
  FAMILY_PLAN_ACCEPT_INVITE: 'v1/charon/acceptFamilyPlanInvite',
  FAMILY_PLAN_REVOKE_INVITE: 'v1/charon/revokeFamilyPlanInvite',
  FAMILY_PLAN_REMOVE_CHILD: 'v1/charon/removeChildFromFamilyPlan',
  FAMILY_PLAN_EXIT: 'v1/charon/exitFromFamilyPlan',
  GET_DISCOUNT_VERIFICATION_INFO: 'v2/charon/eligibilityStatus',
  SUBMIT_DISCOUNT_VERIFICATION: 'v2/charon/eligibilityVerification',

  // APS
  ACTION_MODE: 'v1/action/mode',
  ACTION_PAUSE: 'v1/action/pause',
  ACTION_PREVIOUS: 'v1/action/previous',
  ACTION_REMOVE_THUMB: 'v1/action/removeThumb',
  ACTION_REPEAT: 'v1/action/repeat',
  ACTION_REPLAY: 'v1/action/replay',
  ACTION_SEEK: 'v1/action/seek',
  ACTION_SHUFFLE: 'v1/action/shuffle',
  ACTION_SKIP: 'v1/action/skip',
  ACTION_SNOOZE: 'v1/action/snooze',
  ACTION_THUMB_DOWN: 'v1/action/thumbDown',
  ACTION_THUMB_UP: 'v1/action/thumbUp',
  EVENT_ENDED: 'v1/event/ended',
  EVENT_OFFLINE: 'v1/event/offlineEvents',
  EVENT_PROGRESS: 'v1/event/progress',
  EVENT_STARTED: 'v1/event/started',
  PLAYBACK_CURRENT: 'v1/playback/current',
  PLAYBACK_ITEM: 'v1/playback/item',
  PLAYBACK_ITEM_LIST: 'v1/playback/itemList',
  PLAYBACK_MIDROLL: 'v1/playback/midroll',
  PLAYBACK_PEEK: 'v1/playback/peek',
  PLAYBACK_SOURCE: 'v1/playback/source',

  // Aesop
  AESOP_GET_DETAILS: 'v1/aesop/getDetails',
  AESOP_GET_ALL_THUMBED_EPISODES_BY_PROGRAM: 'v1/aesop/getAllThumbedEpisodesByPodcastProgram',
  AESOP_GET_ALL_EPISODES_BY_PROGRAM: 'v1/aesop/getAllEpisodesByPodcastProgram',
  AESOP_ANNOTATE_OBJECTS: 'v1/aesop/annotateObjects',
  AESOP_REMOVE_EPISODES_FEEDBACK: 'v1/aesop/removeFeedback', // cspell:disable-line

  // PLS
  PLS_GET_DIRECTORY: 'v1/feed/getDirectory',

  // SheerId
  DISCOUNT_ORG_SEARCH_AUTOCOMPLETE: 'rest/0.5/organization/typeahead',

  // Community
  COMMUNITY_SSO: 'community/sso',

  // GraphQL
  GRAPHQL: 'v1/graphql/graphql',

  // ADS - to fetch display ad targeting and flex url's
  ADS_DISPLAY_AD: 'v1/ads/displayAd',

  // APS Rewarded Ads
  CREATE_REWARD: 'v1/rewards/createReward',
} as const);

export const ActionTypes = Object.freeze({
  SEND_SERVICE_ERROR_TO_MIDDLEWARE: 'SEND_SERVICE_ERROR_TO_MIDDLEWARE',
} as const);

/**
 * The exposed, namespaced available API actions.
 */
export const ApiActions = Object.freeze({
  LOGIN_ATTEMPT: 'LOGIN_ATTEMPT',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAIL: 'LOGIN_FAIL',

  LOGOUT_ATTEMPT: 'LOGOUT_ATTEMPT',
  LOGOUT_SUCCESS: 'LOGOUT_SUCCESS',
  LOGOUT_FAIL: 'LOGOUT_FAIL',

  COMMUNITY_SSO_ATTEMPT: 'COMMUNITY_LOGOUT_ATTEMPT',
  COMMUNITY_SSO_SUCCESS: 'COMMUNITY_LOGOUT_SUCCESS',
  COMMUNITY_SSO_FAIL: 'COMMUNITY_LOGOUT_FAIL',

  ANON_LOGIN_ATTEMPT: 'ANON_LOGIN_ATTEMPT',
  ANON_LOGIN_SUCCESS: 'ANON_LOGIN_SUCCESS',
  ANON_LOGIN_FAIL: 'ANON_LOGIN_FAIL',

  DEVICE_LOGIN_ATTEMPT: 'DEVICE_LOGIN_ATTEMPT',
  DEVICE_LOGIN_SUCCESS: 'DEVICE_LOGIN_SUCCESS',
  DEVICE_LOGIN_FAIL: 'DEVICE_LOGIN_FAIL',

  FETCH_STATIONS_ATTEMPT: 'FETCH_STATIONS_ATTEMPT',
  FETCH_STATIONS_SUCCESS: 'FETCH_STATIONS_SUCCESS',
  FETCH_STATIONS_FAIL: 'FETCH_STATIONS_FAIL',

  REGISTER_ATTEMPT: 'REGISTER_ATTEMPT',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS',
  REGISTER_FAIL: 'REGISTER_FAIL',

  EMAIL_PASSWORD_ATTEMPT: 'EMAIL_PASSWORD_ATTEMPT',
  EMAIL_PASSWORD_SUCCESS: 'EMAIL_PASSWORD_SUCCESS',
  EMAIL_PASSWORD_FAIL: 'EMAIL_PASSWORD_FAIL',

  VALIDATE_PASSWORD_RESET_TOKEN_ATTEMPT: 'VALIDATE_PASSWORD_RESET_TOKEN_ATTEMPT',
  VALIDATE_PASSWORD_RESET_TOKEN_SUCCESS: 'VALIDATE_PASSWORD_RESET_TOKEN_SUCCESS',
  VALIDATE_PASSWORD_RESET_TOKEN_FAIL: 'VALIDATE_PASSWORD_RESET_TOKEN_FAIL',

  RESET_PASSWORD_ATTEMPT: 'RESET_PASSWORD_ATTEMPT',
  RESET_PASSWORD_SUCCESS: 'RESET_PASSWORD_SUCCESS',
  RESET_PASSWORD_FAIL: 'RESET_PASSWORD_FAIL',

  UPDATE_ACCOUNT_ATTEMPT: 'UPDATE_ACCOUNT_ATTEMPT',
  UPDATE_ACCOUNT_SUCCESS: 'UPDATE_ACCOUNT_SUCCESS',
  UPDATE_ACCOUNT_FAIL: 'UPDATE_ACCOUNT_FAIL',

  SET_EXPLICIT_PIN_ATTEMPT: 'SET_EXPLICIT_PIN_ATTEMPT',
  SET_EXPLICIT_PIN_SUCCESS: 'SET_EXPLICIT_PIN_SUCCESS',
  SET_EXPLICIT_PIN_FAIL: 'SET_EXPLICIT_PIN_FAIL',

  EMAIL_EXPLICIT_PIN_ATTEMPT: 'EMAIL_EXPLICIT_PIN_ATTEMPT',
  EMAIL_EXPLICIT_PIN_SUCCESS: 'EMAIL_EXPLICIT_PIN_SUCCESS',
  EMAIL_EXPLICIT_PIN_FAIL: 'EMAIL_EXPLICIT_PIN_FAIL',

  UPDATE_SETTINGS_ATTEMPT: 'UPDATE_SETTINGS_ATTEMPT',
  UPDATE_SETTINGS_SUCCESS: 'UPDATE_SETTINGS_SUCCESS',
  UPDATE_SETTINGS_FAIL: 'UPDATE_SETTINGS_FAIL',

  GET_PROFILE_ATTEMPT: 'GET_PROFILE_ATTEMPT',
  GET_PROFILE_SUCCESS: 'GET_PROFILE_SUCCESS',
  GET_PROFILE_FAIL: 'GET_PROFILE_FAIL',

  SET_PROFILE_ATTEMPT: 'SET_PROFILE_ATTEMPT',
  SET_PROFILE_SUCCESS: 'SET_PROFILE_SUCCESS',
  SET_PROFILE_FAIL: 'SET_PROFILE_FAIL',

  ADD_TIRED_SONG_ATTEMPT: 'ADD_TIRED_SONG_ATTEMPT',
  ADD_TIRED_SONG_SUCCESS: 'ADD_TIRED_SONG_SUCCESS',
  ADD_TIRED_SONG_FAIL: 'ADD_TIRED_SONG_FAIL',

  ACCOUNT_MESSAGE_DISMISSED_ATTEMPT: 'ACCOUNT_MESSAGE_DISMISSED_ATTEMPT',
  ACCOUNT_MESSAGE_DISMISSED_SUCCESS: 'ACCOUNT_MESSAGE_DISMISSED_SUCCESS',
  ACCOUNT_MESSAGE_DISMISSED_FAIL: 'ACCOUNT_MESSAGE_DISMISSED_FAIL',

  CREATE_STATION_ATTEMPT: 'CREATE_STATION_ATTEMPT',
  CREATE_STATION_SUCCESS: 'CREATE_STATION_SUCCESS',
  CREATE_STATION_FAIL: 'CREATE_STATION_FAIL',

  REMOVE_STATION_ATTEMPT: 'REMOVE_STATION_ATTEMPT',
  REMOVE_STATION_SUCCESS: 'REMOVE_STATION_SUCCESS',
  REMOVE_STATION_FAIL: 'REMOVE_STATION_FAIL',

  ADD_TRACKING_CODE_ATTEMPT: 'ADD_TRACKING_CODE_ATTEMPT',
  ADD_TRACKING_CODE_SUCCESS: 'ADD_TRACKING_CODE_SUCCESS',
  ADD_TRACKING_CODE_FAIL: 'ADD_TRACKING_CODE_FAIL',

  UPDATE_STATION_ATTEMPT: 'UPDATE_STATION_ATTEMPT',
  UPDATE_STATION_SUCCESS: 'UPDATE_STATION_SUCCESS',
  UPDATE_STATION_FAIL: 'UPDATE_STATION_FAIL',

  ADD_SEED_ATTEMPT: 'ADD_SEED_ATTEMPT',
  ADD_SEED_SUCCESS: 'ADD_SEED_SUCCESS',
  ADD_SEED_FAIL: 'ADD_SEED_FAIL',

  GET_OR_SET_SHUFFLE_STATIONS_ATTEMPT: 'GET_OR_SET_SHUFFLE_STATIONS_ATTEMPT',
  GET_OR_SET_SHUFFLE_STATIONS_SUCCESS: 'GET_OR_SET_SHUFFLE_STATIONS_SUCCESS',
  GET_OR_SET_SHUFFLE_STATIONS_FAIL: 'GET_OR_SET_SHUFFLE_STATIONS_FAIL',

  DELETE_SEED_ATTEMPT: 'DELETE_SEED_ATTEMPT',
  DELETE_SEED_SUCCESS: 'DELETE_SEED_SUCCESS',
  DELETE_SEED_FAIL: 'DELETE_SEED_FAIL',

  GET_STATION_FEEDBACK_ATTEMPT: 'GET_STATION_FEEDBACK_ATTEMPT',
  GET_STATION_FEEDBACK_SUCCESS: 'GET_STATION_FEEDBACK_SUCCESS',
  GET_STATION_FEEDBACK_FAIL: 'GET_STATION_FEEDBACK_FAIL',

  ADD_FEEDBACK_ATTEMPT: 'ADD_FEEDBACK_ATTEMPT',
  ADD_FEEDBACK_SUCCESS: 'ADD_FEEDBACK_SUCCESS',
  ADD_FEEDBACK_FAIL: 'ADD_FEEDBACK_FAIL',

  DELETE_FEEDBACK_ATTEMPT: 'DELETE_FEEDBACK_ATTEMPT',
  DELETE_FEEDBACK_SUCCESS: 'DELETE_FEEDBACK_SUCCESS',
  DELETE_FEEDBACK_FAIL: 'DELETE_FEEDBACK_FAIL',

  TRANSFORM_SHARED_ATTEMPT: 'TRANSFORM_SHARED_ATTEMPT',
  TRANSFORM_SHARED_SUCCESS: 'TRANSFORM_SHARED_SUCCESS',
  TRANSFORM_SHARED_FAIL: 'TRANSFORM_SHARED_FAIL',

  INSERT_ARTIST_MESSAGE_METRIC_ATTEMPT: 'INSERT_ARTIST_MESSAGE_METRIC_ATTEMPT',
  INSERT_ARTIST_MESSAGE_METRIC_SUCCESS: 'INSERT_ARTIST_MESSAGE_METRIC_SUCCESS',
  INSERT_ARTIST_MESSAGE_METRIC_FAIL: 'INSERT_ARTIST_MESSAGE_METRIC_FAIL',

  INSERT_VOICE_TRACK_METRIC_ATTEMPT: 'INSERT_VOICE_TRACK_METRIC_ATTEMPT',
  INSERT_VOICE_TRACK_METRIC_SUCCESS: 'INSERT_VOICE_TRACK_METRIC_SUCCESS',
  INSERT_VOICE_TRACK_METRIC_FAIL: 'INSERT_VOICE_TRACK_METRIC_FAIL',

  GET_ARTIST_EVENTS_ATTEMPT: 'GET_ARTIST_EVENTS_ATTEMPT',
  GET_ARTIST_EVENTS_SUCCESS: 'GET_ARTIST_EVENTS_SUCCESS',
  GET_ARTIST_EVENTS_FAIL: 'GET_ARTIST_EVENTS_FAIL',

  FLAG_MESSAGE_ATTEMPT: 'FLAG_MESSAGE_ATTEMPT',
  FLAG_MESSAGE_SUCCESS: 'FLAG_MESSAGE_SUCCESS',
  FLAG_MESSAGE_FAIL: 'FLAG_MESSAGE_FAIL',

  UPDATE_SHUFFLE_STATION_ATTEMPT: 'UPDATE_SHUFFLE_STATION_ATTEMPT',
  UPDATE_SHUFFLE_STATION_SUCCESS: 'UPDATE_SHUFFLE_STATION_SUCCESS',
  UPDATE_SHUFFLE_STATION_FAIL: 'UPDATE_SHUFFLE_STATION_FAIL',

  VERIFY_HYBRID_STATION_CHECKSUM_ATTEMPT: 'VERIFY_HYBRID_STATION_CHECKSUM_ATTEMPT',
  VERIFY_HYBRID_STATION_CHECKSUM_SUCCESS: 'VERIFY_HYBRID_STATION_CHECKSUM_SUCCESS',
  VERIFY_HYBRID_STATION_CHECKSUM_FAIL: 'VERIFY_HYBRID_STATION_CHECKSUM_FAIL',

  TRACK_STARTED_ATTEMPT: 'TRACK_STARTED_ATTEMPT',
  TRACK_STARTED_SUCCESS: 'TRACK_STARTED_SUCCESS',
  TRACK_STARTED_FAIL: 'TRACK_STARTED_FAIL',

  PLAYBACK_PAUSED_ATTEMPT: 'PLAYBACK_PAUSED_ATTEMPT',
  PLAYBACK_PAUSED_SUCCESS: 'PLAYBACK_PAUSED_SUCCESS',
  PLAYBACK_PAUSED_FAIL: 'PLAYBACK_PAUSED_FAIL',

  PLAYBACK_RESUMED_ATTEMPT: 'PLAYBACK_RESUMED_ATTEMPT',
  PLAYBACK_RESUMED_SUCCESS: 'PLAYBACK_RESUMED_SUCCESS',
  PLAYBACK_RESUMED_FAIL: 'PLAYBACK_RESUMED_FAIL',

  GET_FRAGMENT_ATTEMPT: 'GET_FRAGMENT_ATTEMPT',
  GET_FRAGMENT_SUCCESS: 'GET_FRAGMENT_SUCCESS',
  GET_FRAGMENT_FAIL: 'GET_FRAGMENT_FAIL',

  FETCH_GENRE_CATEGORIES_FAIL: 'FETCH_GENRE_CATEGORIES_FAIL',
  FETCH_GENRE_CATEGORIES_SUCCESS: 'FETCH_GENRE_CATEGORIES_SUCCESS',
  FETCH_GENRE_CATEGORIES_ATTEMPT: 'FETCH_GENRE_CATEGORIES_ATTEMPT',

  FETCH_GENRES_BY_CATEGORY_ID_FAIL: 'FETCH_GENRES_BY_CATEGORY_ID_FAIL',
  FETCH_GENRES_BY_CATEGORY_ID_SUCCESS: 'FETCH_GENRES_BY_CATEGORY_ID_SUCCESS',
  FETCH_GENRES_BY_CATEGORY_ID_ATTEMPT: 'FETCH_GENRES_BY_CATEGORY_ID_ATTEMPT',

  FETCH_NARRATIVE_FAIL: 'FETCH_NARRATIVE_FAIL',
  FETCH_NARRATIVE_SUCCESS: 'FETCH_NARRATIVE_SUCCESS',
  FETCH_NARRATIVE_ATTEMPT: 'FETCH_NARRATIVE_ATTEMPT',

  GET_ADLIST_SUCCESS: 'GET_ADLIST_SUCCESS',
  GET_ADLIST_ATTEMPT: 'GET_ADLIST_ATTEMPT',
  GET_ADLIST_FAIL: 'GET_ADLIST_FAIL',

  FETCH_FULL_LYRICS_ATTEMPT: 'FETCH_FULL_LYRICS_ATTEMPT',
  FETCH_FULL_LYRICS_SUCCESS: 'FETCH_FULL_LYRICS_SUCCESS',
  FETCH_FULL_LYRICS_FAIL: 'FETCH_FULL_LYRICS_FAIL',

  ARTIST_SHARE_ATTEMPT: 'ARTIST_SHARE_ATTEMPT',
  ARTIST_SHARE_SUCCESS: 'ARTIST_SHARE_SUCCESS',
  ARTIST_SHARE_FAIL: 'ARTIST_SHARE_FAIL',

  SEARCH_TERM_UPDATE: 'SEARCH_TERM_UPDATE',
  T3_SEARCH_TERM_UPDATE: 'T3_SEARCH_TERM_UPDATE',

  SEARCH_ATTEMPT: 'SEARCH_ATTEMPT',
  SEARCH_SUCCESS: 'SEARCH_SUCCESS',
  SEARCH_FAIL: 'SEARCH_FAIL',

  SEARCH_AUTOCOMPLETE_ATTEMPT: 'SEARCH_AUTOCOMPLETE_ATTEMPT',
  SEARCH_AUTOCOMPLETE_SUCCESS: 'SEARCH_AUTOCOMPLETE_SUCCESS',
  SEARCH_AUTOCOMPLETE_FAIL: 'SEARCH_AUTOCOMPLETE_FAIL',

  SEARCH_RECOMMENDATIONS_ATTEMPT: 'SEARCH_RECOMMENDATIONS_ATTEMPT',
  SEARCH_RECOMMENDATIONS_SUCCESS: 'SEARCH_RECOMMENDATIONS_SUCCESS',
  SEARCH_RECOMMENDATIONS_FAIL: 'SEARCH_RECOMMENDATIONS_FAIL',

  GET_STATION_DETAILS_ATTEMPT: 'GET_STATION_DETAILS_ATTEMPT',
  GET_STATION_DETAILS_SUCCESS: 'GET_STATION_DETAILS_SUCCESS',
  GET_STATION_DETAILS_FAIL: 'GET_STATION_DETAILS_FAIL',

  GET_FEEDBACK_ATTEMPT: 'GET_FEEDBACK_ATTEMPT',
  GET_FEEDBACK_SUCCESS: 'GET_FEEDBACK_SUCCESS',
  GET_FEEDBACK_FAIL: 'GET_FEEDBACK_FAIL',

  GET_SEED_SUGGESTIONS_ATTEMPT: 'GET_SEED_SUGGESTIONS_ATTEMPT',
  GET_SEED_SUGGESTIONS_SUCCESS: 'GET_SEED_SUGGESTIONS_SUCCESS',
  GET_SEED_SUGGESTIONS_FAIL: 'GET_SEED_SUGGESTIONS_FAIL',

  GET_SEEDS_ATTEMPT: 'GET_SEEDS_ATTEMPT',
  GET_SEEDS_SUCCESS: 'GET_SEEDS_SUCCESS',
  GET_SEEDS_FAIL: 'GET_SEEDS_FAIL',

  USE_SKIP_REWARD_ATTEMPT: 'USE_SKIP_REWARD_ATTEMPT',
  USE_SKIP_REWARD_SUCCESS: 'USE_SKIP_REWARD_SUCCESS',
  USE_SKIP_REWARD_FAIL: 'USE_SKIP_REWARD_FAIL',

  USE_REPLAY_REWARD_ATTEMPT: 'USE_REPLAY_REWARD_ATTEMPT',
  USE_REPLAY_REWARD_SUCCESS: 'USE_REPLAY_REWARD_SUCCESS',
  USE_REPLAY_REWARD_FAIL: 'USE_REPLAY_REWARD_FAIL',

  START_VALUE_EXCHANGE_ATTEMPT: 'START_VALUE_EXCHANGE_ATTEMPT',
  START_VALUE_EXCHANGE_SUCCESS: 'START_VALUE_EXCHANGE_SUCCESS',
  START_VALUE_EXCHANGE_FAIL: 'START_VALUE_EXCHANGE_FAIL',

  FACEBOOK_CONNECT_ATTEMPT: 'FACEBOOK_CONNECT_ATTEMPT',
  FACEBOOK_CONNECT_SUCCESS: 'FACEBOOK_CONNECT_SUCCESS',
  FACEBOOK_CONNECT_FAIL: 'FACEBOOK_CONNECT_FAIL',

  FACEBOOK_DISCONNECT_ATTEMPT: 'FACEBOOK_DISCONNECT_ATTEMPT',
  FACEBOOK_DISCONNECT_SUCCESS: 'FACEBOOK_DISCONNECT_SUCCESS',
  FACEBOOK_DISCONNECT_FAIL: 'FACEBOOK_DISCONNECT_FAIL',

  GET_REPLAY_TRACK_ATTEMPT: 'GET_REPLAY_TRACK_ATTEMPT',
  GET_REPLAY_TRACK_SUCCESS: 'GET_REPLAY_TRACK_SUCCESS',
  GET_REPLAY_TRACK_FAIL: 'GET_REPLAY_TRACK_FAIL',

  UPDATE_BILLING_INFO_V2_ATTEMPT: 'UPDATE_BILLING_INFO_V2_ATTEMPT',
  UPDATE_BILLING_INFO_V2_SUCCESS: 'UPDATE_BILLING_INFO_V2_SUCCESS',
  UPDATE_BILLING_INFO_V2_FAIL: 'UPDATE_BILLING_INFO_V2_FAIL',

  GET_SUBSCRIPTION_INFO_V2_ATTEMPT: 'GET_SUBSCRIPTION_INFO_V2_ATTEMPT',
  GET_SUBSCRIPTION_INFO_V2_SUCCESS: 'GET_SUBSCRIPTION_INFO_V2_SUCCESS',
  GET_SUBSCRIPTION_INFO_V2_FAIL: 'GET_SUBSCRIPTION_INFO_V2_FAIL',

  GET_AVAILABLE_PRODUCTS_ATTEMPT: 'GET_AVAILABLE_PRODUCTS_ATTEMPT',
  GET_AVAILABLE_PRODUCTS_SUCCESS: 'GET_AVAILABLE_PRODUCTS_SUCCESS',
  GET_AVAILABLE_PRODUCTS_FAIL: 'GET_AVAILABLE_PRODUCTS_FAIL',

  GET_SPECIAL_OFFER_V2_ATTEMPT: 'GET_SPECIAL_OFFER_V2_ATTEMPT',
  GET_SPECIAL_OFFER_V2_SUCCESS: 'GET_SPECIAL_OFFER_V2_SUCCESS',
  GET_SPECIAL_OFFER_V2_FAIL: 'GET_SPECIAL_OFFER_V2_FAIL',

  GET_SKU_DETAIL_ATTEMPT: 'GET_SKU_DETAIL_ATTEMPT',
  GET_SKU_DETAIL_SUCCESS: 'GET_SKU_DETAIL_SUCCESS',
  GET_SKU_DETAIL_FAIL: 'GET_SKU_DETAIL_FAIL',

  REGISTER_IMPRESSION_ATTEMPT: 'REGISTER_IMPRESSION_ATTEMPT',
  REGISTER_IMPRESSION_SUCCESS: 'REGISTER_IMPRESSION_SUCCESS',
  REGISTER_IMPRESSION_FAIL: 'REGISTER_IMPRESSION_FAIL',

  REGISTER_VIDEO_AD_IMPRESSION_ATTEMPT: 'REGISTER_VIDEO_AD_IMPRESSION_ATTEMPT',
  REGISTER_VIDEO_AD_IMPRESSION_SUCCESS: 'REGISTER_VIDEO_AD_IMPRESSION_SUCCESS',
  REGISTER_VIDEO_AD_IMPRESSION_FAIL: 'REGISTER_VIDEO_AD_IMPRESSION_FAIL',

  REGISTER_AUDIO_PROGRAMMATIC_IMPRESSION_ATTEMPT: 'REGISTER_AUDIO_PROGRAMMATIC_IMPRESSION_ATTEMPT',
  REGISTER_AUDIO_PROGRAMMATIC_IMPRESSION_SUCCESS: 'REGISTER_AUDIO_PROGRAMMATIC_IMPRESSION_SUCCESS',
  REGISTER_AUDIO_PROGRAMMATIC_IMPRESSION_FAIL: 'REGISTER_AUDIO_PROGRAMMATIC_IMPRESSION_FAIL',

  SEND_NEW_SITE_FEEDBACK_ATTEMPT: 'SEND_NEW_SITE_FEEDBACK_ATTEMPT',
  SEND_NEW_SITE_FEEDBACK_SUCCESS: 'SEND_NEW_SITE_FEEDBACK_SUCCESS',
  SEND_NEW_SITE_FEEDBACK_FAIL: 'SEND_NEW_SITE_FEEDBACK_FAIL',

  DELETE_LISTENER_ATTEMPT: 'DELETE_LISTENER_ATTEMPT',
  DELETE_LISTENER_SUCCESS: 'DELETE_LISTENER_SUCCESS',
  DELETE_LISTENER_FAIL: 'DELETE_LISTENER_FAIL',

  LOG_OUT_ALL_DEVICES_ATTEMPT: 'LOG_OUT_ALL_DEVICES_ATTEMPT',
  LOG_OUT_ALL_DEVICES_SUCCESS: 'LOG_OUT_ALL_DEVICES_SUCCESS',
  LOG_OUT_ALL_DEVICES_FAIL: 'LOG_OUT_ALL_DEVICES_FAIL',

  SET_TWITTER_TOKENS_ATTEMPT: 'SET_TWITTER_TOKENS_ATTEMPT',
  SET_TWITTER_TOKENS_SUCCESS: 'SET_TWITTER_TOKENS_SUCCESS',
  SET_TWITTER_TOKENS_FAIL: 'SET_TWITTER_TOKENS_FAIL',

  GET_REQUEST_TOKEN_ATTEMPT: 'GET_REQUEST_TOKEN_ATTEMPT',
  GET_REQUEST_TOKEN_SUCCESS: 'GET_REQUEST_TOKEN_SUCCESS',
  GET_REQUEST_TOKEN_FAIL: 'GET_REQUEST_TOKEN_FAIL',

  GET_AUTH_TOKEN_ATTEMPT: 'GET_AUTH_TOKEN_ATTEMPT',
  GET_AUTH_TOKEN_SUCCESS: 'GET_AUTH_TOKEN_SUCCESS',
  GET_AUTH_TOKEN_FAIL: 'GET_AUTH_TOKEN_FAIL',

  POST_STATUS_UPDATE_ATTEMPT: 'POST_STATUS_UPDATE_ATTEMPT',
  POST_STATUS_UPDATE_SUCCESS: 'POST_STATUS_UPDATE_SUCCESS',
  POST_STATUS_UPDATE_FAIL: 'POST_STATUS_UPDATE_FAIL',

  SEND_STATS_EVENTS_ATTEMPT: 'SEND_STATS_EVENTS_ATTEMPT',
  SEND_STATS_EVENTS_SUCCESS: 'SEND_STATS_EVENTS_SUCCESS',
  SEND_STATS_EVENTS_FAIL: 'SEND_STATS_EVENTS_FAIL',

  BROKEN_AD_ATTEMPT: 'BROKEN_AD_ATTEMPT',
  BROKEN_AD_SUCCESS: 'BROKEN_AD_SUCCESS',
  BROKEN_AD_FAIL: 'BROKEN_AD_FAIL',

  ACQUIRE_PRODUCT_ATTEMPT: 'ACQUIRE_PRODUCT_ATTEMPT',
  ACQUIRE_PRODUCT_SUCCESS: 'ACQUIRE_PRODUCT_SUCCESS',
  ACQUIRE_PRODUCT_FAIL: 'ACQUIRE_PRODUCT_FAIL',

  PREPARE_TO_ACQUIRE_PAYPAL_PRODUCT_ATTEMPT: 'PREPARE_TO_ACQUIRE_PAYPAL_PRODUCT_ATTEMPT',
  PREPARE_TO_ACQUIRE_PAYPAL_PRODUCT_SUCCESS: 'PREPARE_TO_ACQUIRE_PAYPAL_PRODUCT_SUCCESS',
  PREPARE_TO_ACQUIRE_PAYPAL_PRODUCT_FAIL: 'PREPARE_TO_ACQUIRE_PAYPAL_PRODUCT_FAIL',

  GET_CREDIT_CARD_V2_ATTEMPT: 'GET_CREDIT_CARD_V2_ATTEMPT',
  GET_CREDIT_CARD_V2_SUCCESS: 'GET_CREDIT_CARD_V2_SUCCESS',
  GET_CREDIT_CARD_V2_FAIL: 'GET_CREDIT_CARD_V2_FAIL',

  CANCEL_CURRENT_PRODUCT_ATTEMPT: 'CANCEL_CURRENT_PRODUCT_ATTEMPT',
  CANCEL_CURRENT_PRODUCT_SUCCESS: 'CANCEL_CURRENT_PRODUCT_SUCCESS',
  CANCEL_CURRENT_PRODUCT_FAIL: 'CANCEL_CURRENT_PRODUCT_FAIL',

  INTERACTIVE_RADIO_SET_MODE_ATTEMPT: 'INTERACTIVE_RADIO_SET_MODE_ATTEMPT',
  INTERACTIVE_RADIO_SET_MODE_SUCCESS: 'INTERACTIVE_RADIO_SET_MODE_SUCCESS',
  INTERACTIVE_RADIO_SET_MODE_FAIL: 'INTERACTIVE_RADIO_SET_MODE_FAIL',

  INTERACTIVE_RADIO_GET_MODES_ATTEMPT: 'INTERACTIVE_RADIO_GET_MODES_ATTEMPT',
  INTERACTIVE_RADIO_GET_MODES_SUCCESS: 'INTERACTIVE_RADIO_GET_MODES_SUCCESS',
  INTERACTIVE_RADIO_GET_MODES_FAIL: 'INTERACTIVE_RADIO_GET_MODES_FAIL',

  CATALOG_ANNOTATE_OBJECTS_ATTEMPT: 'CATALOG_ANNOTATE_OBJECTS_ATTEMPT',
  CATALOG_ANNOTATE_OBJECTS_SUCCESS: 'CATALOG_ANNOTATE_OBJECTS_SUCCESS',
  CATALOG_ANNOTATE_OBJECTS_FAIL: 'CATALOG_ANNOTATE_OBJECTS_FAIL',

  CATALOG_ANNOTATE_OBJECTS_SIMPLE_ATTEMPT: 'CATALOG_ANNOTATE_OBJECTS_SIMPLE_ATTEMPT',
  CATALOG_ANNOTATE_OBJECTS_SIMPLE_SUCCESS: 'CATALOG_ANNOTATE_OBJECTS_SIMPLE_SUCCESS',
  CATALOG_ANNOTATE_OBJECTS_SIMPLE_FAIL: 'CATALOG_ANNOTATE_OBJECTS_SIMPLE_FAIL',

  CATALOG_GET_DETAILS_ATTEMPT: 'CATALOG_GET_DETAILS_ATTEMPT',
  CATALOG_GET_DETAILS_SUCCESS: 'CATALOG_GET_DETAILS_SUCCESS',
  CATALOG_GET_DETAILS_FAIL: 'CATALOG_GET_DETAILS_FAIL',

  CATALOG_GET_ARTIST_DISCOGRAPHY_ATTEMPT: 'CATALOG_GET_ARTIST_DISCOGRAPHY_ATTEMPT',
  CATALOG_GET_ARTIST_DISCOGRAPHY_SUCCESS: 'CATALOG_GET_ARTIST_DISCOGRAPHY_SUCCESS',
  CATALOG_GET_ARTIST_DISCOGRAPHY_FAIL: 'CATALOG_GET_ARTIST_DISCOGRAPHY_FAIL',

  CATALOG_GET_ALL_ARTIST_TRACKS_ATTEMPT: 'CATALOG_GET_ALL_ARTIST_TRACKS_ATTEMPT',
  CATALOG_GET_ALL_ARTIST_TRACKS_SUCCESS: 'CATALOG_GET_ALL_ARTIST_TRACKS_SUCCESS',
  CATALOG_GET_ALL_ARTIST_TRACKS_FAIL: 'CATALOG_GET_ALL_ARTIST_TRACKS_FAIL',

  CATALOG_GET_DETAILS_WITH_COLLABORATIONS_ATTEMPT:
    'CATALOG_GET_DETAILS_WITH_COLLABORATIONS_ATTEMPT',
  CATALOG_GET_DETAILS_WITH_COLLABORATIONS_SUCCESS:
    'CATALOG_GET_DETAILS_WITH_COLLABORATIONS_SUCCESS',
  CATALOG_GET_DETAILS_WITH_COLLABORATIONS_FAIL: 'CATALOG_GET_DETAILS_WITH_COLLABORATIONS_FAIL',

  CATALOG_GET_ARTIST_DISCOGRAPHY_WITH_COLLABORATIONS_ATTEMPT:
    'CATALOG_GET_ARTIST_DISCOGRAPHY_WITH_COLLABORATIONS_ATTEMPT',
  CATALOG_GET_ARTIST_DISCOGRAPHY_WITH_COLLABORATIONS_SUCCESS:
    'CATALOG_GET_ARTIST_DISCOGRAPHY_WITH_COLLABORATIONS_SUCCESS',
  CATALOG_GET_ARTIST_DISCOGRAPHY_WITH_COLLABORATIONS_FAIL:
    'CATALOG_GET_ARTIST_DISCOGRAPHY_WITH_COLLABORATIONS_FAIL',

  CATALOG_GET_ALL_ARTIST_TRACKS_WITH_COLLABORATIONS_ATTEMPT:
    'CATALOG_GET_ALL_ARTIST_TRACKS_WITH_COLLABORATIONS_ATTEMPT',
  CATALOG_GET_ALL_ARTIST_TRACKS_WITH_COLLABORATIONS_SUCCESS:
    'CATALOG_GET_ALL_ARTIST_TRACKS_WITH_COLLABORATIONS_SUCCESS',
  CATALOG_GET_ALL_ARTIST_TRACKS_WITH_COLLABORATIONS_FAIL:
    'CATALOG_GET_ALL_ARTIST_TRACKS_WITH_COLLABORATIONS_FAIL',

  CATALOG_SORT_OBJECTS_ATTEMPT: 'CATALOG_SORT_OBJECTS_ATTEMPT',
  CATALOG_SORT_OBJECTS_SUCCESS: 'CATALOG_SORT_OBJECTS_SUCCESS',
  CATALOG_SORT_OBJECTS_FAIL: 'CATALOG_SORT_OBJECTS_FAIL',

  COLLECTIONS_GET_VERSION_ATTEMPT: 'COLLECTIONS_GET_VERSION_ATTEMPT',
  COLLECTIONS_GET_VERSION_SUCCESS: 'COLLECTIONS_GET_VERSION_SUCCESS',
  COLLECTIONS_GET_VERSION_FAIL: 'COLLECTIONS_GET_VERSION_FAIL',

  COLLECTIONS_GET_ITEMS_ATTEMPT: 'COLLECTIONS_GET_ITEMS_ATTEMPT',
  COLLECTIONS_GET_ITEMS_SUCCESS: 'COLLECTIONS_GET_ITEMS_SUCCESS',
  COLLECTIONS_GET_ITEMS_FAIL: 'COLLECTIONS_GET_ITEMS_FAIL',

  COLLECTIONS_GET_ITEMS_BY_ARTIST_ATTEMPT: 'COLLECTIONS_GET_ITEMS_BY_ARTIST_ATTEMPT',
  COLLECTIONS_GET_ITEMS_BY_ARTIST_SUCCESS: 'COLLECTIONS_GET_ITEMS_BY_ARTIST_SUCCESS',
  COLLECTIONS_GET_ITEMS_BY_ARTIST_FAIL: 'COLLECTIONS_GET_ITEMS_BY_ARTIST_FAIL',

  COLLECTIONS_GET_SORTED_BY_TYPES_V6_ATTEMPT: 'COLLECTIONS_GET_SORTED_BY_TYPES_V6_ATTEMPT',
  COLLECTIONS_GET_SORTED_BY_TYPES_V6_SUCCESS: 'COLLECTIONS_GET_SORTED_BY_TYPES_V6_SUCCESS',
  COLLECTIONS_GET_SORTED_BY_TYPES_V6_FAIL: 'COLLECTIONS_GET_SORTED_BY_TYPES_V6_FAIL',

  COLLECTIONS_GET_SORTED_BY_TYPES_ATTEMPT: 'COLLECTIONS_GET_SORTED_BY_TYPES_ATTEMPT',
  COLLECTIONS_GET_SORTED_BY_TYPES_SUCCESS: 'COLLECTIONS_GET_SORTED_BY_TYPES_SUCCESS',
  COLLECTIONS_GET_SORTED_BY_TYPES_FAIL: 'COLLECTIONS_GET_SORTED_BY_TYPES_FAIL',

  COLLECTIONS_GET_SORTED_PLAYLISTS_ATTEMPT: 'COLLECTIONS_GET_SORTED_PLAYLISTS_ATTEMPT',
  COLLECTIONS_GET_SORTED_PLAYLISTS_SUCCESS: 'COLLECTIONS_GET_SORTED_PLAYLISTS_SUCCESS',
  COLLECTIONS_GET_SORTED_PLAYLISTS_FAIL: 'COLLECTIONS_GET_SORTED_PLAYLISTS_FAIL',

  COLLECTIONS_ADD_ITEM_ATTEMPT: 'COLLECTIONS_ADD_ITEM_ATTEMPT',
  COLLECTIONS_ADD_ITEM_SUCCESS: 'COLLECTIONS_ADD_ITEM_SUCCESS',
  COLLECTIONS_ADD_ITEM_FAIL: 'COLLECTIONS_ADD_ITEM_FAIL',

  COLLECTIONS_REMOVE_ITEM_ATTEMPT: 'COLLECTIONS_REMOVE_ITEM_ATTEMPT',
  COLLECTIONS_REMOVE_ITEM_SUCCESS: 'COLLECTIONS_REMOVE_ITEM_SUCCESS',
  COLLECTIONS_REMOVE_ITEM_FAIL: 'COLLECTIONS_REMOVE_ITEM_FAIL',

  PLAYLISTS_CREATE_ATTEMPT: 'PLAYLISTS_CREATE_ATTEMPT',
  PLAYLISTS_CREATE_SUCCESS: 'PLAYLISTS_CREATE_SUCCESS',
  PLAYLISTS_CREATE_FAIL: 'PLAYLISTS_CREATE_FAIL',

  PLAYLISTS_ANNOTATE_PLAYLISTS_ATTEMPT: 'PLAYLISTS_ANNOTATE_PLAYLISTS_ATTEMPT',
  PLAYLISTS_ANNOTATE_PLAYLISTS_SUCCESS: 'PLAYLISTS_ANNOTATE_PLAYLISTS_SUCCESS',
  PLAYLISTS_ANNOTATE_PLAYLISTS_FAIL: 'PLAYLISTS_ANNOTATE_PLAYLISTS_FAIL',

  PLAYLISTS_GET_TRACKS_ATTEMPT: 'PLAYLISTS_GET_TRACKS_ATTEMPT',
  PLAYLISTS_GET_TRACKS_SUCCESS: 'PLAYLISTS_GET_TRACKS_SUCCESS',
  PLAYLISTS_GET_TRACKS_FAIL: 'PLAYLISTS_GET_TRACKS_FAIL',

  PLAYLISTS_EDIT_TRACKS_ATTEMPT: 'PLAYLISTS_EDIT_TRACKS_ATTEMPT',
  PLAYLISTS_EDIT_TRACKS_SUCCESS: 'PLAYLISTS_EDIT_TRACKS_SUCCESS',
  PLAYLISTS_EDIT_TRACKS_FAIL: 'PLAYLISTS_EDIT_TRACKS_FAIL',

  PLAYLISTS_APPEND_ITEMS_ATTEMPT: 'PLAYLISTS_APPEND_ITEMS_ATTEMPT',
  PLAYLISTS_APPEND_ITEMS_SUCCESS: 'PLAYLISTS_APPEND_ITEMS_SUCCESS',
  PLAYLISTS_APPEND_ITEMS_FAIL: 'PLAYLISTS_APPEND_ITEMS_FAIL',

  PLAYLISTS_DELETE_TRACKS_ATTEMPT: 'PLAYLISTS_DELETE_TRACKS_ATTEMPT',
  PLAYLISTS_DELETE_TRACKS_SUCCESS: 'PLAYLISTS_DELETE_TRACKS_SUCCESS',
  PLAYLISTS_DELETE_TRACKS_FAIL: 'PLAYLISTS_DELETE_TRACKS_FAIL',

  PLAYLISTS_SET_DETAILS_ATTEMPT: 'PLAYLISTS_SET_DETAILS_ATTEMPT',
  PLAYLISTS_SET_DETAILS_SUCCESS: 'PLAYLISTS_SET_DETAILS_SUCCESS',
  PLAYLISTS_SET_DETAILS_FAIL: 'PLAYLISTS_SET_DETAILS_FAIL',

  SOD_SEARCH_ATTEMPT: 'SOD_SEARCH_ATTEMPT',
  SOD_SEARCH_SUCCESS: 'SOD_SEARCH_SUCCESS',
  SOD_SEARCH_FAIL: 'SOD_SEARCH_FAIL',

  PODS_GET_PLAYLIST_AUTOFILL_SONGS_ATTEMPT: 'PODS_GET_PLAYLIST_AUTOFILL_SONGS_ATTEMPT',
  PODS_GET_PLAYLIST_AUTOFILL_SONGS_SUCCESS: 'PODS_GET_PLAYLIST_AUTOFILL_SONGS_SUCCESS',
  PODS_GET_PLAYLIST_AUTOFILL_SONGS_FAIL: 'PODS_GET_PLAYLIST_AUTOFILL_SONGS_FAIL',

  PODS_GET_PLAYLIST_SEARCH_RECOMMENDATIONS_ATTEMPT:
    'PODS_GET_PLAYLIST_SEARCH_RECOMMENDATIONS_ATTEMPT',
  PODS_GET_PLAYLIST_SEARCH_RECOMMENDATIONS_SUCCESS:
    'PODS_GET_PLAYLIST_SEARCH_RECOMMENDATIONS_SUCCESS',
  PODS_GET_PLAYLIST_SEARCH_RECOMMENDATIONS_FAIL: 'PODS_GET_PLAYLIST_SEARCH_RECOMMENDATIONS_FAIL',

  // Family Plan
  FAMILY_PLAN_GET_RECORD_ATTEMPT: 'FAMILY_PLAN_GET_RECORD_ATTEMPT',
  FAMILY_PLAN_GET_RECORD_SUCCESS: 'FAMILY_PLAN_GET_RECORD_SUCCESS',
  FAMILY_PLAN_GET_RECORD_FAIL: 'FAMILY_PLAN_GET_RECORD_FAIL',

  FAMILY_PLAN_GET_PARENT_INFO_ATTEMPT: 'FAMILY_PLAN_GET_PARENT_INFO_ATTEMPT',
  FAMILY_PLAN_GET_PARENT_INFO_SUCCESS: 'FAMILY_PLAN_GET_PARENT_INFO_SUCCESS',
  FAMILY_PLAN_GET_PARENT_INFO_FAIL: 'FAMILY_PLAN_GET_PARENT_INFO_FAIL',

  FAMILY_PLAN_CREATE_INVITE_ATTEMPT: 'FAMILY_PLAN_CREATE_INVITE_ATTEMPT',
  FAMILY_PLAN_CREATE_INVITE_SUCCESS: 'FAMILY_PLAN_CREATE_INVITE_SUCCESS',
  FAMILY_PLAN_CREATE_INVITE_FAIL: 'FAMILY_PLAN_CREATE_INVITE_FAIL',

  FAMILY_PLAN_RESEND_INVITE_ATTEMPT: 'FAMILY_PLAN_RESEND_INVITE_ATTEMPT',
  FAMILY_PLAN_RESEND_INVITE_SUCCESS: 'FAMILY_PLAN_RESEND_INVITE_SUCCESS',
  FAMILY_PLAN_RESEND_INVITE_FAIL: 'FAMILY_PLAN_RESEND_INVITE_FAIL',

  FAMILY_PLAN_ACCEPT_INVITE_ATTEMPT: 'FAMILY_PLAN_ACCEPT_INVITE_ATTEMPT',
  FAMILY_PLAN_ACCEPT_INVITE_SUCCESS: 'FAMILY_PLAN_ACCEPT_INVITE_SUCCESS',
  FAMILY_PLAN_ACCEPT_INVITE_FAIL: 'FAMILY_PLAN_ACCEPT_INVITE_FAIL',

  FAMILY_PLAN_REVOKE_INVITE_ATTEMPT: 'FAMILY_PLAN_REVOKE_INVITE_ATTEMPT',
  FAMILY_PLAN_REVOKE_INVITE_SUCCESS: 'FAMILY_PLAN_REVOKE_INVITE_SUCCESS',
  FAMILY_PLAN_REVOKE_INVITE_FAIL: 'FAMILY_PLAN_REVOKE_INVITE_FAIL',

  FAMILY_PLAN_REMOVE_CHILD_ATTEMPT: 'FAMILY_PLAN_REMOVE_CHILD_ATTEMPT',
  FAMILY_PLAN_REMOVE_CHILD_SUCCESS: 'FAMILY_PLAN_REMOVE_CHILD_SUCCESS',
  FAMILY_PLAN_REMOVE_CHILD_FAIL: 'FAMILY_PLAN_REMOVE_CHILD_FAIL',

  FAMILY_PLAN_EXIT_ATTEMPT: 'FAMILY_PLAN_EXIT_ATTEMPT',
  FAMILY_PLAN_EXIT_SUCCESS: 'FAMILY_PLAN_EXIT_SUCCESS',
  FAMILY_PLAN_EXIT_FAIL: 'FAMILY_PLAN_EXIT_FAIL',

  // Student/Military (Sheer ID discount verification)
  GET_DISCOUNT_VERIFICATION_INFO_ATTEMPT: 'GET_DISCOUNT_VERIFICATION_INFO_ATTEMPT',
  GET_DISCOUNT_VERIFICATION_INFO_SUCCESS: 'GET_DISCOUNT_VERIFICATION_INFO_SUCCESS',
  GET_DISCOUNT_VERIFICATION_INFO_FAIL: 'GET_DISCOUNT_VERIFICATION_INFO_FAIL',

  SUBMIT_DISCOUNT_VERIFICATION_ATTEMPT: 'SUBMIT_DISCOUNT_VERIFICATION_ATTEMPT',
  SUBMIT_DISCOUNT_VERIFICATION_SUCCESS: 'SUBMIT_DISCOUNT_VERIFICATION_SUCCESS',
  SUBMIT_DISCOUNT_VERIFICATION_FAIL: 'SUBMIT_DISCOUNT_VERIFICATION_FAIL',

  // APS
  ACTION_MODE_ATTEMPT: 'ACTION_MODE_ATTEMPT',
  ACTION_MODE_SUCCESS: 'ACTION_MODE_SUCCESS',
  ACTION_MODE_FAIL: 'ACTION_MODE_FAIL',

  ACTION_PAUSE_ATTEMPT: 'ACTION_PAUSE_ATTEMPT',
  ACTION_PAUSE_SUCCESS: 'ACTION_PAUSE_SUCCESS',
  ACTION_PAUSE_FAIL: 'ACTION_PAUSE_FAIL',

  ACTION_PREVIOUS_ATTEMPT: 'ACTION_PREVIOUS_ATTEMPT',
  ACTION_PREVIOUS_SUCCESS: 'ACTION_PREVIOUS_SUCCESS',
  ACTION_PREVIOUS_FAIL: 'ACTION_PREVIOUS_FAIL',

  ACTION_REMOVE_THUMB_ATTEMPT: 'ACTION_REMOVE_THUMB_ATTEMPT',
  ACTION_REMOVE_THUMB_SUCCESS: 'ACTION_REMOVE_THUMB_SUCCESS',
  ACTION_REMOVE_THUMB_FAIL: 'ACTION_REMOVE_THUMB_FAIL',

  ACTION_REPEAT_ATTEMPT: 'ACTION_REPEAT_ATTEMPT',
  ACTION_REPEAT_SUCCESS: 'ACTION_REPEAT_SUCCESS',
  ACTION_REPEAT_FAIL: 'ACTION_REPEAT_FAIL',

  ACTION_REPLAY_ATTEMPT: 'ACTION_REPLAY_ATTEMPT',
  ACTION_REPLAY_SUCCESS: 'ACTION_REPLAY_SUCCESS',
  ACTION_REPLAY_FAIL: 'ACTION_REPLAY_FAIL',

  ACTION_SEEK_ATTEMPT: 'ACTION_SEEK_ATTEMPT',
  ACTION_SEEK_SUCCESS: 'ACTION_SEEK_SUCCESS',
  ACTION_SEEK_FAIL: 'ACTION_SEEK_FAIL',

  ACTION_SHUFFLE_ATTEMPT: 'ACTION_SHUFFLE_ATTEMPT',
  ACTION_SHUFFLE_SUCCESS: 'ACTION_SHUFFLE_SUCCESS',
  ACTION_SHUFFLE_FAIL: 'ACTION_SHUFFLE_FAIL',

  ACTION_SKIP_ATTEMPT: 'ACTION_SKIP_ATTEMPT',
  ACTION_SKIP_SUCCESS: 'ACTION_SKIP_SUCCESS',
  ACTION_SKIP_FAIL: 'ACTION_SKIP_FAIL',

  ACTION_SNOOZE_ATTEMPT: 'ACTION_SNOOZE_ATTEMPT',
  ACTION_SNOOZE_SUCCESS: 'ACTION_SNOOZE_SUCCESS',
  ACTION_SNOOZE_FAIL: 'ACTION_SNOOZE_FAIL',

  ACTION_THUMB_DOWN_ATTEMPT: 'ACTION_THUMB_DOWN_ATTEMPT',
  ACTION_THUMB_DOWN_SUCCESS: 'ACTION_THUMB_DOWN_SUCCESS',
  ACTION_THUMB_DOWN_FAIL: 'ACTION_THUMB_DOWN_FAIL',

  ACTION_THUMB_UP_ATTEMPT: 'ACTION_THUMB_UP_ATTEMPT',
  ACTION_THUMB_UP_SUCCESS: 'ACTION_THUMB_UP_SUCCESS',
  ACTION_THUMB_UP_FAIL: 'ACTION_THUMB_UP_FAIL',

  EVENT_ENDED_ATTEMPT: 'EVENT_ENDED_ATTEMPT',
  EVENT_ENDED_SUCCESS: 'EVENT_ENDED_SUCCESS',
  EVENT_ENDED_FAIL: 'EVENT_ENDED_FAIL',

  EVENT_OFFLINE_ATTEMPT: 'EVENT_OFFLINE_ATTEMPT',
  EVENT_OFFLINE_SUCCESS: 'EVENT_OFFLINE_SUCCESS',
  EVENT_OFFLINE_FAIL: 'EVENT_OFFLINE_FAIL',

  EVENT_PROGRESS_ATTEMPT: 'EVENT_PROGRESS_ATTEMPT',
  EVENT_PROGRESS_SUCCESS: 'EVENT_PROGRESS_SUCCESS',
  EVENT_PROGRESS_FAIL: 'EVENT_PROGRESS_FAIL',

  EVENT_STARTED_ATTEMPT: 'EVENT_STARTED_ATTEMPT',
  EVENT_STARTED_SUCCESS: 'EVENT_STARTED_SUCCESS',
  EVENT_STARTED_FAIL: 'EVENT_STARTED_FAIL',

  PLAYBACK_CURRENT_ATTEMPT: 'PLAYBACK_CURRENT_ATTEMPT',
  PLAYBACK_CURRENT_SUCCESS: 'PLAYBACK_CURRENT_SUCCESS',
  PLAYBACK_CURRENT_FAIL: 'PLAYBACK_CURRENT_FAIL',

  PLAYBACK_ITEM_ATTEMPT: 'PLAYBACK_ITEM_ATTEMPT',
  PLAYBACK_ITEM_SUCCESS: 'PLAYBACK_ITEM_SUCCESS',
  PLAYBACK_ITEM_FAIL: 'PLAYBACK_ITEM_FAIL',

  PLAYBACK_ITEM_LIST_ATTEMPT: 'PLAYBACK_ITEM_LIST_ATTEMPT',
  PLAYBACK_ITEM_LIST_SUCCESS: 'PLAYBACK_ITEM_LIST_SUCCESS',
  PLAYBACK_ITEM_LIST_FAIL: 'PLAYBACK_ITEM_LIST_FAIL',

  PLAYBACK_MIDROLL_ATTEMPT: 'PLAYBACK_MIDROLL_ATTEMPT',
  PLAYBACK_MIDROLL_SUCCESS: 'PLAYBACK_MIDROLL_SUCCESS',
  PLAYBACK_MIDROLL_FAIL: 'PLAYBACK_MIDROLL_FAIL',

  PLAYBACK_PEEK_ATTEMPT: 'PLAYBACK_PEEK_ATTEMPT',
  PLAYBACK_PEEK_SUCCESS: 'PLAYBACK_PEEK_SUCCESS',
  PLAYBACK_PEEK_FAIL: 'PLAYBACK_PEEK_FAIL',

  PLAYBACK_SOURCE_ATTEMPT: 'PLAYBACK_SOURCE_ATTEMPT',
  PLAYBACK_SOURCE_SUCCESS: 'PLAYBACK_SOURCE_SUCCESS',
  PLAYBACK_SOURCE_FAIL: 'PLAYBACK_SOURCE_FAIL',

  AESOP_GET_DETAILS_SUCCESS: 'AESOP_GET_DETAILS_SUCCESS',
  AESOP_GET_DETAILS_ATTEMPT: 'AESOP_GET_DETAILS_ATTEMPT',
  AESOP_GET_DETAILS_FAIL: 'AESOP_GET_DETAILS_FAIL',

  AESOP_GET_ALL_THUMBED_EPISODES_BY_PROGRAM_SUCCESS:
    'AESOP_GET_ALL_THUMBED_EPISODES_BY_PROGRAM_SUCCESS',
  AESOP_GET_ALL_THUMBED_EPISODES_BY_PROGRAM_ATTEMPT:
    'AESOP_GET_ALL_THUMBED_EPISODES_BY_PROGRAM_ATTEMPT',
  AESOP_GET_ALL_THUMBED_EPISODES_BY_PROGRAM_FAIL: 'AESOP_GET_ALL_THUMBED_EPISODES_BY_PROGRAM_FAIL',

  AESOP_GET_ALL_EPISODES_BY_PROGRAM_SUCCESS: 'AESOP_GET_ALL_EPISODES_BY_PROGRAM_SUCCESS',
  AESOP_GET_ALL_EPISODES_BY_PROGRAM_ATTEMPT: 'AESOP_GET_ALL_EPISODES_BY_PROGRAM_ATTEMPT',
  AESOP_GET_ALL_EPISODES_BY_PROGRAM_FAIL: 'AESOP_GET_ALL_EPISODES_BY_PROGRAM_FAIL',

  AESOP_ANNOTATE_OBJECTS_SUCCESS: 'AESOP_ANNOTATE_OBJECTS_SUCCESS',
  AESOP_ANNOTATE_OBJECTS_ATTEMPT: 'AESOP_ANNOTATE_OBJECTS_ATTEMPT',
  AESOP_ANNOTATE_OBJECTS_FAIL: 'AESOP_ANNOTATE_OBJECTS_FAIL',

  AESOP_REMOVE_EPISODES_FEEDBACK_SUCCESS: 'AESOP_REMOVE_EPISODES_FEEDBACK_SUCCESS',
  AESOP_REMOVE_EPISODES_FEEDBACK_ATTEMPT: 'AESOP_REMOVE_EPISODES_FEEDBACK_ATTEMPT',
  AESOP_REMOVE_EPISODES_FEEDBACK_FAIL: 'AESOP_REMOVE_EPISODES_FEEDBACK_FAIL',

  GET_MUSIC_RECOMMENDATION_SUCCESS: 'GET_MUSIC_RECOMMENDATION_SUCCESS',
  GET_MUSIC_RECOMMENDATION_ATTEMPT: 'GET_MUSIC_RECOMMENDATION_ATTEMPT',
  GET_MUSIC_RECOMMENDATION_FAIL: 'GET_MUSIC_RECOMMENDATION_FAIL',

  GET_MUSIC_CATALOG_SUCCESS: 'GET_MUSIC_CATALOG_SUCCESS',
  GET_MUSIC_CATALOG_ATTEMPT: 'GET_MUSIC_CATALOG_ATTEMPT',
  GET_MUSIC_CATALOG_FAIL: 'GET_MUSIC_CATALOG_FAIL',

  PLS_GET_DIRECTORY_SUCCESS: 'PLS_GET_DIRECTORY_SUCCESS',
  PLS_GET_DIRECTORY_ATTEMPT: 'PLS_GET_DIRECTORY_ATTEMPT',
  PLS_GET_DIRECTORY_FAIL: 'PLS_GET_DIRECTORY_FAIL',

  DISCOUNT_ORG_SEARCH_AUTOCOMPLETE_SUCCESS: 'DISCOUNT_ORG_SEARCH_AUTOCOMPLETE_SUCCESS',
  DISCOUNT_ORG_SEARCH_AUTOCOMPLETE_ATTEMPT: 'DISCOUNT_ORG_SEARCH_AUTOCOMPLETE_ATTEMPT',
  DISCOUNT_ORG_SEARCH_AUTOCOMPLETE_FAIL: 'DISCOUNT_ORG_SEARCH_AUTOCOMPLETE_FAIL',

  GET_EXPERIMENTS_FOR_LISTENER_ATTEMPT: 'GET_EXPERIMENTS_FOR_LISTENER_ATTEMPT',
  GET_EXPERIMENTS_FOR_LISTENER_SUCCESS: 'GET_EXPERIMENTS_FOR_LISTENER_SUCCESS',
  GET_EXPERIMENTS_FOR_LISTENER_FAIL: 'GET_EXPERIMENTS_FOR_LISTENER_FAIL',

  GRAPHQL_ATTEMPT: 'GRAPHQL_ATTEMPT',
  GRAPHQL_SUCCESS: 'GRAPHQL_SUCCESS',
  GRAPHQL_FAIL: 'GRAPHQL_FAIL',

  ADS_DISPLAY_AD_ATTEMPT: 'ADS_DISPLAY_AD_ATTEMPT',
  ADS_DISPLAY_AD_SUCCESS: 'ADS_DISPLAY_AD_SUCCESS',
  ADS_DISPLAY_AD_FAIL: 'ADS_DISPLAY_AD_FAIL',

  CREATE_REWARD_ATTEMPT: 'CREATE_REWARD_ATTEMPT',
  CREATE_REWARD_SUCCESS: 'CREATE_REWARD_SUCCESS',
  CREATE_REWARD_FAIL: 'CREATE_REWARD_FAIL',
} as const);

export const AUTH_ACTIONS = Object.freeze([
  ApiActions.LOGIN_ATTEMPT,
  ApiActions.ANON_LOGIN_ATTEMPT,
  ApiActions.DEVICE_LOGIN_ATTEMPT,
] as const);

export default ApiActions;
